import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {useIntl} from "umi";
import {ErrorTip, Form, LoadingTip, Select} from 'tea-component';
import {useRequest} from "ahooks";
import { getTableInfoList } from '@dbl/services/dataSource';
import { FieldTypeIcon } from '@dbl/core/dataSource';
import SvgIcon from "@home/components/SvgIcon";
import {LANGUAGE_EN} from "@home/_config/config";
import {DATALAB_SPLIT_STR} from '@dbl/core/global';
import {getLocalText} from "@/pages/datalab/utils/utils";

/**
 * 【自定义同名字段修复】字段下拉组件
 *
 * 背景：同一数据表被多张图表使用时，不同图表上的自定义字段可能同名。
 * 旧版仅用字段名作 Select value，会导致选项冲突；选中自定义字段后又用单图 chartId
 * 拉列表，其它图上的同名字段会从下拉中消失（显示错乱）。
 *
 * 方案概要：
 * 1. UI 层：自定义字段 option.value 拼接 chartId（DATALAB_SPLIT_STR），供 Select 区分与回显；
 *    保存/上抛的 column 仍为纯字段名（onColumnChange 内 split 去掉后缀）。
 * 2. 请求层：listChartId / ref.chartId 为「使用该数据表的全部图表 id（逗号拼接）」，
 *    始终拉全量字段列表；customColumnChartId 为「当前选中字段所属单图 id」，仅用于回显。
 * 3. 兼容层：旧配置无 chartId 后缀时，校验与回显退化为按字段名匹配。
 */
const ColumnOptionText = ({column, chartName}: any) => {
  
  const columnName = column?.display_name_en || column?.display_name || column?.name;
  // const chartName = isEn ? chartNameEn : chartNameCn;
  const suffixText = chartName ? `(${chartName})` : '';
  
  return column ? (<div title={`${columnName}${suffixText}`} className={'dbl-exit-filter-scope-charts-join-field'}><SvgIcon
    className={FieldTypeIcon[column.type] || "abc"} type={FieldTypeIcon[column.type] || "abc"}/>{columnName}<span className={'suffix'}>{suffixText}</span>
  </div>) : null;
};

export interface IFilterColumnRef {
  getDateColumns: () => any[];
}

export interface IFilterColumnProps {
  status?: string;
  dataset?: string;
  column?: string;
  onChange?: (value: string, context: any) => void;
  /** 选中字段所属图表（单 id），用于 Select 回显 */
  chartId?: string;
  /**
   * 拉取字段列表用的多图 chart_id（逗号拼接），优先于 columnStatusRef.chartId。
   * 与 chartId 分离：避免选中自定义字段后列表请求收窄为单图导致同名字段消失。
   */
  listChartId?: string;
  columnStatusRef?: any;
  changeStatus?: (key: string, status: string) => void;
  gameCode?: string;
}

const FilterColumn = forwardRef<IFilterColumnRef, IFilterColumnProps>((props, ref) => {
  const {
    status,
    dataset,
    column,
    onChange,
    chartId,
    listChartId: listChartIdProp,
    columnStatusRef,
    changeStatus,
    gameCode,
  } = props;
  const {formatMessage, locale} = useIntl();
  const isEn = locale === LANGUAGE_EN;
  
  const isClickedOptionRef = useRef(false);
  const [open, setOpen] = useState(false);
  
  const { data, run, loading, error} = useRequest(getTableInfoList, {
    manual: true,
    onError(e){ console.log("报表编辑筛选器请求Column选项错误: ", e)},
  });

  const buildOptionValue = (columnName: string, optionChartId?: string | number) => {
    // 有 chartId 表示来自某张图的自定义字段，value 需带后缀才能在 Select 中唯一标识
    const chartIdStr =
      optionChartId !== undefined && optionChartId !== null && optionChartId !== ''
        ? String(optionChartId)
        : '';
    return chartIdStr
      ? `${columnName}${DATALAB_SPLIT_STR}${chartIdStr}`
      : columnName;
  };

  // 选中字段所属图表（单 id），仅用于 Select 回显，不参与列表请求
  const selectedChartId = useMemo(() => {
    const raw =
      columnStatusRef?.current?.customColumnChartId ?? chartId;
    return raw !== undefined && raw !== null && String(raw) !== ''
      ? String(raw)
      : '';
  }, [chartId, column]);

  const resolvedListChartId = useMemo(() => {
    const raw =
      listChartIdProp || columnStatusRef?.current?.chartId;
    return raw !== undefined && raw !== null && String(raw) !== ''
      ? String(raw)
      : '';
  }, [listChartIdProp, dataset]);

  // 列表请求使用数据表关联的全部 chart_id（逗号拼接），避免选中后收窄为单图导致后缀项消失
  useEffect(() => {
    if (!dataset) {
      return;
    }
    if (
      resolvedListChartId &&
      columnStatusRef?.current &&
      columnStatusRef.current.chartId !== resolvedListChartId
    ) {
      columnStatusRef.current.chartId = resolvedListChartId;
    }
    const listId = resolvedListChartId || undefined;
    run([dataset], gameCode || '', 'datalab', listId);
  }, [dataset, gameCode, run, resolvedListChartId, columnStatusRef]);

  const selectValue = useMemo(() => {
    if (!column) {
      return '';
    }
    return buildOptionValue(column, selectedChartId || undefined);
  }, [column, selectedChartId]);

  const options = useMemo(() => {
    const newOptions: any[] = [];
    const tableList: any[] = (data as any)?.data ?? (data as any) ?? [];
    tableList?.forEach?.((table: any) => {

      table?.column_list?.forEach((columnInfo: any) => {
        const optionChartId =
          table?.chart_id !== undefined && table?.chart_id !== null
            ? String(table.chart_id)
            : '';
        const optionValue = buildOptionValue(
          columnInfo?.name,
          optionChartId || undefined,
        );
        newOptions.push({
          ...columnInfo,
          // 自定义字段在不同图表中 name 可能相同，value 需拼接 chart_id 供 Select 区分
          value: optionValue,
          rowKey: optionChartId
            ? `${columnInfo?.name}-${optionChartId}`
            : `${columnInfo?.name}-${table?.table_id ?? ''}-${newOptions.length}`,
          text: (<ColumnOptionText column={columnInfo} chartName={getLocalText(isEn, table?.chart_name_cn, table?.chart_name)}/>),
          tableId: table?.table_id,
          chartId: table?.chart_id,
          chartNameCn: table?.chart_name_cn ?? '',
          chartNameEn: table?.chart_name ?? ''
        });
        
        // 为了兼容 旧数据的情况
        // 在点击确认按钮, 校验Scope 的关联字段和Column的字段类型是否相同时, 使用了columnStatusRef.current.columnType进行判断.
        // 但旧数据中 没有columnType这个值, 所以只能在选项加载回来的时候, 手动赋值.
        //
       
        !columnStatusRef.current.columnType &&
          selectValue === buildOptionValue(columnInfo?.name, optionChartId || undefined) &&
          (columnStatusRef.current.columnType = columnInfo.type);
      });
    });
    
    return newOptions;
  }, [data, isEn, selectValue]);

  // 缓存日期列计算结果
  const dateColumns = useMemo(() => {
    const columns: any[] = []; // 数据表中自带的日期字段
    const customColumns: any[] = []; // 图表中自定义的日期字段
    // 是有两个数组是为了保证顺序和拖拉拽页面保持一致.
    const tableList: any[] = (data as any)?.data ?? (data as any) ?? [];
    tableList?.forEach?.((table: any) => {
      table?.column_list?.forEach((columnInfo: any) => {
        if (columnInfo.type === 'date') {
          const info = {
            ...columnInfo,
            table_id: table?.table_id,
            table_code: table?.table_code ?? 't0',
            chart_id: table?.chart_id,
            chart_name_cn: table?.chart_name_cn ?? '',
            chart_name_en: table?.chart_name ?? ''
          };
          
          table?.chart_id ? customColumns.push(info) : columns.push(info);
        }
      });
    });
    return [...columns, ...customColumns];
  }, [data]);

  // 暴露 ref 方法
  useImperativeHandle(ref, () => ({
    getDateColumns: () => dateColumns,
  }), [dateColumns]);

  // 筛选器状态
  useEffect(() => {
    if(!options?.length) return;
    const isSelected = options?.some((opt) => {
      if (opt?.value === selectValue) {
        return true;
      }
      // 旧数据无 chartId 后缀时，仅按字段名匹配
      if (!selectedChartId) {
        return opt?.value?.split(DATALAB_SPLIT_STR)?.[0] === column;
      }
      return false;
    });
    // 数据集和字段都有值，但是未被选中表示字段被删除了
    if (!isSelected && dataset && column) {
      changeStatus?.('column', 'field_error');
    } else {
      changeStatus?.('column', 'success');
    };
  }, [options, dataset, column, selectValue, selectedChartId]);

  const onColumnChange = (value: string, context: any) => {
    // Select 回显要求 value 与 options.value 完全一致（含 @DATALAB@ 后缀）；
    // 向上层/onConfirm 只传递纯字段名，与后端及其它模块的 column 字段约定保持一致。
    const optionChartId = context?.option?.chartId;
    columnStatusRef.current.customColumnChartId =
      optionChartId !== undefined && optionChartId !== null
        ? String(optionChartId)
        : '';
    const newValue = value.split(DATALAB_SPLIT_STR)[0];
    onChange?.(newValue, context);
  };

  const message = useMemo(()=> {
    const messageMap: any = {
      'success': '',
      'error': formatMessage({ id: 'dbl_common_cannot_be_empty' }),
      'field_error': formatMessage({ id: 'dbl_common_filter_field_error' }),
      'reference_field_error': formatMessage({ id: 'dbl_common_filter_reference_field_error' }),
    };
    return messageMap?.[status || ''] || '';
  },[formatMessage, status]);

  return (
    <Form.Item 
      label={formatMessage({ id: 'dbl_create_dashboard_column' })}
      showStatusIcon={false}
      status={['error' , 'field_error' , 'reference_field_error']?.includes(status || '') ? 'error' : 'success'}
      // eslint-disable-next-line no-nested-ternary
      message={message}>
      <Select
        className={'dbl-exit-filter-column-selector'}
        overlayClassName={'dbl-exit-filter-column-overlay'}
        open={open}
        onClose={() => {isClickedOptionRef.current = false}}
        onOpenChange={(open) => {
          // 在日期类型时, 点击选项时不能关闭下拉选项的浮窗, 所以这里用 isClickedOptionRef.current 来记录用户点击选项的动作
          // isClickedOptionRef.current 为 true时, 说明用户点击了选项, 不关闭浮窗
          // 当用户点击选项时, 会先执行onClose事件, 再执行onOpenChange事件, 在onClose中将isClickedOptionRef.current设置为false, 这样既不会在点击选项后关闭浮窗, 也不会点击空白处时关闭不了浮窗
          (!isClickedOptionRef.current) && setOpen(open);
        }}
        matchButtonWidth
        appearance="button"
        options={(loading || error) ? [] : options}
        searchable
        filter={((inputValue, option) => {
          if (!inputValue) return true;
          
          const keyword = inputValue?.trim()?.toUpperCase(); // 搜索文本
          const opt: any = option as any;
          const columnName = opt?.display_name || opt?.display_name_en; // 字段名称
          const chartName = String((opt?.chartNameCn ?? '') + (opt?.chartNameEn ?? '')); // 自定义字段所在的图表名称
          
          // 搜索匹配 字段重命名, 字段原始名称, 字段所在的图表名称
          return (
            columnName?.toUpperCase()?.includes(keyword) ||
            opt?.name?.toUpperCase()?.includes(keyword) ||
            chartName.toUpperCase()?.includes(inputValue)
          );
        })}
        value={selectValue}
        onChange={onColumnChange}
        placeholder={formatMessage({id: 'dbl_create_dashboard_column_placeholder'})}
        tips={loading ? <LoadingTip /> : (error ? <ErrorTip/> : '')}
      />
    </Form.Item>
  );
});

FilterColumn.displayName = 'FilterColumn';

export default FilterColumn;
